import axios from 'axios';
import Typesense from 'typesense';

// --- URL helpers (avoid /api/api and missing /events) ---
const ensureApiBase = (apiUrl) => {
  const base = String(apiUrl || '').replace(/\/+$/, ''); // trim trailing slash
  // if base already ends with /api keep it, else add /api
  return /\/api$/.test(base) ? base : `${base}/api`;
};

const buildFeedUrl = (apiUrl) => {
  const apiBase = ensureApiBase(apiUrl);
  // server mounts feed at /api/events/search/feed
  return `${apiBase}/events/search/feed`;
};

// Initialize Typesense client
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST;
const TYPESENSE_PORT = parseInt(process.env.REACT_APP_TYPESENSE_PORT, 10);
const TYPESENSE_PROTOCOL = process.env.REACT_APP_TYPESENSE_PROTOCOL;
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY;

let typesenseClient;
try {
  if (TYPESENSE_HOST && TYPESENSE_PORT && TYPESENSE_PROTOCOL && TYPESENSE_API_KEY) {
    typesenseClient = new Typesense.Client({
      nodes: [
        {
          host: TYPESENSE_HOST,
          port: TYPESENSE_PORT,
          protocol: TYPESENSE_PROTOCOL
        }
      ],
      apiKey: TYPESENSE_API_KEY,
      connectionTimeoutSeconds: 5,
      retryIntervalSeconds: 0.1,
      numRetries: 3
    });
    console.log('✅ searchEvents: Typesense client initialized successfully');
  } else {
    console.warn('⚠️ searchEvents: Typesense environment variables missing');
    typesenseClient = null;
  }
} catch (error) {
  console.error('❌ searchEvents: Failed to initialize Typesense client:', error);
  typesenseClient = null;
}

// Helper function to ensure dates are properly formatted as strings
const formatDateToString = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  if (date instanceof Date) {
    try {
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Error converting date to string:', e);
      return '';
    }
  }
  return String(date);
};

// Helper function to get map radius filter
const getMapRadiusFilter = (bounds) => {
  if (!bounds?.sw || !bounds?.ne ||
      bounds.sw.lat == null || bounds.sw.lng == null ||
      bounds.ne.lat == null || bounds.ne.lng == null) {
    return null;
  }

  const centerLat = (bounds.sw.lat + bounds.ne.lat) / 2;
  const centerLng = (bounds.sw.lng + bounds.ne.lng) / 2;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371;

  const dLat = toRad(bounds.ne.lat - bounds.sw.lat);
  const dLng = toRad(bounds.ne.lng - bounds.sw.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(bounds.sw.lat)) *
      Math.cos(toRad(bounds.ne.lat)) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const diagonalKm = earthRadius * c;

  const dynamicRadius = Math.max(50, Math.floor(diagonalKm * 1.5)); // Increased minimum radius and multiplier

  return `${centerLat}, ${centerLng}, ${dynamicRadius} km`;
};

/**
 * Helper: Convert UNIX timestamp (seconds) to ISO string
 */
const tsToIsoClient = (ts) => {
  if (Number.isFinite(Number(ts))) {
    return new Date(Number(ts) * 1000).toISOString();
  }
  return null;
};

/**
 * Normalize a Typesense review document to event-compatible shape for map markers
 */
const normalizeReviewForMap = (doc) => {
  if (!doc) return null;

  let latitude = doc.latitude ?? null;
  let longitude = doc.longitude ?? null;

  if ((latitude == null || longitude == null) && doc.location) {
    if (typeof doc.location === 'string' && doc.location.includes(',')) {
      const [la, lo] = doc.location.split(',').map(s => parseFloat(s.trim()));
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    } else if (Array.isArray(doc.location) && doc.location.length >= 2) {
      const [la, lo] = doc.location;
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    }
  }

  // Build the same media_items shape the /events/feed endpoint returns, so a
  // review opened from the map renders through ReviewMediaCarousel exactly like
  // one opened from the list. Typesense only carries the main image + main video,
  // which is all a card preview needs; the detail view re-fetches the full set.
  const imageItems = doc.main_image_url
    ? [{ type: 'image', url: doc.main_image_url, display_order: 0 }]
    : [];
  const videoItems = doc.main_video_url
    ? [{ type: 'video', url: doc.main_video_url, display_order: 0 }]
    : [];
  const contentType = (doc.main_content_type || 'image').toLowerCase();
  const isVideoMain = contentType === 'video' || contentType === 'video_link';
  const mediaItems = isVideoMain
    ? [...videoItems, ...imageItems]
    : [...imageItems, ...videoItems];

  return {
    ...doc,
    id: 'review_' + doc.id,
    event_id: 'review_' + doc.id,
    event_name: doc.place_name || 'Untitled Review',
    event_location: doc.location_name || '',
    event_image_link: doc.main_image_url || null,
    event_date: tsToIsoClient(doc.created_at_ts) || null,
    event_end_date: null,
    event_summary: doc.review_content_text || '',
    main_category: 'Review',
    _source: 'review',
    ...(mediaItems.length > 0 ? { media_items: mediaItems } : {}),
    latitude,
    longitude
  };
};

/**
 * Normalize a Typesense venue document to event-compatible shape for map markers
 */
const normalizeVenueForMap = (doc) => {
  if (!doc) return null;

  let latitude = doc.latitude ?? null;
  let longitude = doc.longitude ?? null;

  if ((latitude == null || longitude == null) && doc.location) {
    if (typeof doc.location === 'string' && doc.location.includes(',')) {
      const [la, lo] = doc.location.split(',').map(s => parseFloat(s.trim()));
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    } else if (Array.isArray(doc.location) && doc.location.length >= 2) {
      const [la, lo] = doc.location;
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    }
  }

  return {
    ...doc,
    id: 'venue_' + doc.venue_id,
    event_id: 'venue_' + doc.venue_id,
    event_name: doc.venue_name || 'Untitled Venue',
    event_location: doc.location_name || doc.google_address || '',
    event_image_link: doc.logo_url || doc.main_image_url || null,
    event_date: null, // Venues don't have dates
    event_end_date: null,
    event_summary: doc.description || '',
    main_category: 'Venue',
    sub_category: doc.venue_type || 'restaurant',
    _source: 'venue',
    latitude,
    longitude
  };
};

// Main search function
export async function searchEvents({
  bounds,        // { ne: {lat,lng}, sw: {lat,lng} } or similar
  startDate,     // 'YYYY-MM-DD'
  endDate,       // 'YYYY-MM-DD'
  type,          // 'Category' or a specific main_category
  subCategory,   // NEW: optional sub_category filter
  query = '',    // optional free text
  userLocation,  // {lat, lng} optional — the user's real GPS position
  // Explicit centre for the radius filter, used when the caller wants radiusKm
  // honoured even though GPS is unavailable (e.g. Home's progressive radius
  // expansion falls back to the map centre). Takes precedence over
  // userLocation; omit it to keep the old userLocation-or-bounds behaviour.
  searchCenter,
  language,      // TH, EN, CN
  applyLocationFilterForAuto = false, // NEW: Force location filter even with empty query
  radiusKm = 10, // NEW: Radius in km for location filter (default 10)
  disableFallback = false,    // When true, return genuine 0 instead of falling back to unfiltered results
  source = ['events'],        // NEW: Which collections to search
  excludeCategories = [],     // NEW: Categories to exclude
  apiUrl = process.env.REACT_APP_API_URL,
  signal,        // AbortSignal for cancellation
  logger = console
}) {
  // Validate required parameters
  if (!apiUrl) {
    logger.error('🔎 searchEvents error: API URL not provided');
    return [];
  }

  if (!typesenseClient) {
    logger.error('🔎 searchEvents error: Typesense client not initialized');
    return [];
  }

  // Format dates
  const startDateString = formatDateToString(startDate);
  const endDateString = formatDateToString(endDate);

  logger.log('🔎 searchEvents params:', {
    bounds: bounds ? 'SET' : 'NOT SET',
    startDate: startDateString,
    endDate: endDateString,
    type,
    subCategory: subCategory || 'not set',
    query: query || 'empty',
    language,
    userLocation: userLocation ? 'SET' : 'NOT SET'
  });

  try {
    // Build filters
    const filters = [];

    if (language) {
      filters.push(`language:"${language}"`);
    }

    // Category filter
    if (type && type !== 'Category') {
      filters.push(`main_category:"${type}"`);
    }

    // Sub-category filter
    if (subCategory && subCategory !== 'All' && subCategory !== '') {
      filters.push(`sub_category:"${subCategory}"`);
    }

    // Exclude categories (negative filter)
    const safeExclude = Array.isArray(excludeCategories) ? excludeCategories : [];
    if (safeExclude.length > 0) {
      safeExclude.forEach(cat => {
        filters.push(`main_category:!="${cat}"`);
      });
    }

    // Date filters - simplified logic
    if (startDateString && endDateString) {
      const start = Math.floor(new Date(startDateString).setHours(0, 0, 0, 0) / 1000);
      const end = Math.floor(new Date(endDateString).setHours(23, 59, 59, 999) / 1000);

      const dateConditions = [
        `(event_date_ts:>=${start} && event_date_ts:<=${end})`,
        `(event_end_date_ts:>=${start} && event_end_date_ts:<=${end})`,
        `(event_date_ts:<=${start} && event_end_date_ts:>=${end})`
      ];

      filters.push(`(${dateConditions.join(' || ')})`);
    }

    // Location filter — prefer an explicit centre + radiusKm, then the user's
    // GPS position, and only fall back to the map bounds when we have neither.
    // NOTE: the bounds fallback ignores radiusKm completely, so any caller
    // doing progressive radius expansion must supply a centre or every pass
    // will produce the exact same filter.
    let mapRadiusFilter = null;

    const radiusCenter =
      (Number.isFinite(Number(searchCenter?.lat)) && Number.isFinite(Number(searchCenter?.lng)))
        ? { lat: Number(searchCenter.lat), lng: Number(searchCenter.lng), _label: 'search centre' }
        : (userLocation?.lat && userLocation?.lng)
          ? { lat: userLocation.lat, lng: userLocation.lng, _label: 'user location' }
          : null;

    if (radiusCenter) {
      mapRadiusFilter = `${radiusCenter.lat}, ${radiusCenter.lng}, ${radiusKm} km`;
      logger.log(`🗺️ Using ${radiusCenter._label} filter:`, mapRadiusFilter);
    } else if (bounds) {
      // Fallback: derive dynamic radius from bounds
      mapRadiusFilter = getMapRadiusFilter(bounds);
      if (mapRadiusFilter) {
        logger.log('🗺️ Using bounds-based location filter:', mapRadiusFilter);
      }
    }

    if (mapRadiusFilter) {
      // Apply location filter if:
      // 1. Query is not empty (manual search), OR
      // 2. applyLocationFilterForAuto is true (forced auto search around location)
      if (query && query.trim()) {
        filters.push(`location:(${mapRadiusFilter})`);
        logger.log('🗺️ Applied location filter for manual search:', mapRadiusFilter);
      } else if (applyLocationFilterForAuto) {
        filters.push(`location:(${mapRadiusFilter})`);
        logger.log('🗺️ Applied location filter for auto search (forced):', mapRadiusFilter);
      } else {
        logger.log('🗺️ Skipping location filter for auto-search to get more results');
      }
    }

    // Build search parameters
    const searchParams = {
      q: query || '*',
      query_by: 'event_name,searchableContent',
      per_page: 250, // Increased from 40 to 250 to allow more results
      typo_tokens_threshold: 1,
      num_typos: 1,
      prefix: true,
      sort_by: '_text_match:desc'
    };

    if (filters.length) {
      searchParams.filter_by = filters.join(' && ');
    }

    logger.log('🔎 searchEvents Typesense params:', {
      query: searchParams.q,
      filters: searchParams.filter_by || 'None',
      per_page: searchParams.per_page
    });

    // Check if request was aborted before Typesense call
    if (signal?.aborted) {
      logger.log('🚫 searchEvents: Request aborted before Typesense call');
      return [];
    }

    // Normalize sources
    const sources = Array.isArray(source) ? source : [source || 'events'];
    let allResults = [];

    // Search EVENTS collection if source includes 'events'
    if (sources.includes('events')) {
      // Perform Typesense search
      const result = await typesenseClient.collections('events').documents().search(searchParams);

      // Check if request was aborted after Typesense call
      if (signal?.aborted) {
        logger.log('🚫 searchEvents: Request aborted after Typesense call');
        return [];
      }

      logger.log('📈 searchEvents events results:', {
        found: result.found || 0,
        hits: result.hits?.length || 0
      });

      if (result && Array.isArray(result.hits) && result.hits.length > 0) {
        // Extract event IDs from search results
        const ids = result.hits.map(hit => {
          const id = hit.document.id;
          const numId = typeof id === 'string' ? parseInt(id, 10) : id;
          return isNaN(numId) ? null : numId;
        }).filter(id => id !== null);

        logger.log(`🔍 searchEvents: Extracted ${ids.length} valid event IDs`);

        if (ids.length > 0 && !signal?.aborted) {
          // Fetch from backend
          const backendResponse = await axios.post(`${apiUrl}/events/batch`, {
            eventIds: ids,
            language: language
          }, signal ? { signal } : {});

          if (!signal?.aborted) {
            logger.log(`📥 searchEvents: ${backendResponse.data.length} events from backend`);
            allResults.push(...(backendResponse.data || []));
          }
        }
      } else if (result?.hits?.length === 0 && searchParams.filter_by && !disableFallback) {
        // Retry without category filter (skip when caller wants genuine 0 for radius expansion)
        logger.log('🔄 searchEvents: No results with filters, trying basic search...');
        const basicParams = {
          q: query || '*',
          query_by: 'event_name,searchableContent',
          per_page: 250,
          filter_by: language ? `language:"${language}"` : undefined
        };
        const retryResult = await typesenseClient.collections('events').documents().search(basicParams);
        if (!signal?.aborted && retryResult?.hits?.length > 0) {
          const ids = retryResult.hits.map(hit => {
            const id = hit.document.id;
            const numId = typeof id === 'string' ? parseInt(id, 10) : id;
            return isNaN(numId) ? null : numId;
          }).filter(id => id !== null);
          if (ids.length > 0) {
            const backendResponse = await axios.post(`${apiUrl}/events/batch`, {
              eventIds: ids,
              language: language
            }, signal ? { signal } : {});
            if (!signal?.aborted) {
              allResults.push(...(backendResponse.data || []));
            }
          }
        }
      }
    }

    // Search REVIEWS collection if source includes 'reviews'
    if (sources.includes('reviews') && typesenseClient && !signal?.aborted) {
      try {
        const reviewFilters = [];

        // NOTE: no language filter here, deliberately.
        //
        // reviews.language records the language the review was WRITTEN in, not a
        // per-language row: a single review row carries place_name_th/en/cn and
        // review_content_th/en/cn, so one document serves every UI language. Every
        // review created from the admin RClipper page is language:'TH'. Filtering
        // on it meant an EN or CN user saw zero reviews on the map even though the
        // English text was sitting right there in the same document.
        //
        // This matches the backend feed, which already documents the same choice
        // (backend/src/routes/events/search.js — "language filter intentionally
        // NOT applied to reviews"). Events and venues keep their language filter,
        // because those DO store one row per language.

        // Date filter for reviews: always use 1.5 years in the past (not user-selected dates)
        const reviewStartTs = Math.floor((Date.now() - 1.5 * 365.25 * 24 * 3600 * 1000) / 1000);
        reviewFilters.push(`created_at_ts:>=${reviewStartTs}`);

        // Location filter for reviews
        if (mapRadiusFilter && (query?.trim() || applyLocationFilterForAuto)) {
          reviewFilters.push(`location:(${mapRadiusFilter})`);
        }

        const reviewParams = {
          q: query || '*',
          query_by: 'place_name,searchableContent',
          per_page: 250,
          sort_by: '_text_match:desc'
        };

        if (reviewFilters.length) {
          reviewParams.filter_by = reviewFilters.join(' && ');
        }

        logger.log('🔎 searchEvents: Searching reviews collection', reviewParams);

        const reviewResult = await typesenseClient.collections('reviews').documents().search(reviewParams);

        if (!signal?.aborted && reviewResult?.hits?.length > 0) {
          const reviewEvents = reviewResult.hits
            .map(hit => normalizeReviewForMap(hit.document))
            .filter(r => r && r.latitude != null && r.longitude != null);

          logger.log(`📥 searchEvents: ${reviewEvents.length} reviews for map`);
          allResults.push(...reviewEvents);
        }
      } catch (reviewErr) {
        logger.warn('⚠️ searchEvents: Reviews search failed:', reviewErr.message);
      }
    }

    // Search VENUES collection if source includes 'venues'
    if (sources.includes('venues') && typesenseClient && !signal?.aborted) {
      try {
        const venueFilters = [];

        if (language) {
          venueFilters.push(`language:"${language}"`);
        }

        // Location filter for venues
        if (mapRadiusFilter && (query?.trim() || applyLocationFilterForAuto)) {
          venueFilters.push(`location:(${mapRadiusFilter})`);
        }

        const venueParams = {
          q: query || '*',
          query_by: 'venue_name,searchableContent',
          per_page: 250,
          sort_by: '_text_match:desc'
        };

        if (venueFilters.length) {
          venueParams.filter_by = venueFilters.join(' && ');
        }

        logger.log('🔎 searchEvents: Searching venues collection', venueParams);

        const venueResult = await typesenseClient.collections('venues').documents().search(venueParams);

        logger.log('🔎 searchEvents: Venues search result:', {
          found: venueResult?.found || 0,
          hits: venueResult?.hits?.length || 0
        });

        if (!signal?.aborted && venueResult?.hits?.length > 0) {
          // Extract venue IDs from search results
          const venueIds = venueResult.hits.map(hit => {
            const id = hit.document.venue_id || hit.document.id;
            // Handle both numeric IDs and potential string IDs
            const numId = typeof id === 'string' ? parseInt(id, 10) : id;
            return isNaN(numId) ? null : numId;
          }).filter(id => id !== null);

          logger.log(`🔍 searchEvents: Extracted ${venueIds.length} valid venue IDs`, venueIds.slice(0, 10));

          if (venueIds.length > 0) {
            // Fetch from backend
            logger.log(`🔍 searchEvents: Calling ${apiUrl}/venues/batch with`, { venueIds: venueIds.slice(0, 5), language });

            const backendResponse = await axios.post(`${apiUrl}/venues/batch`, {
              venueIds: venueIds,
              language: language
            }, signal ? { signal } : {});

            logger.log(`📥 searchEvents: Backend returned ${backendResponse.data?.length || 0} venues`);

            if (!signal?.aborted) {
              // Normalize venues to event-compatible shape for map markers
              const normalizedVenues = backendResponse.data
                .map(venue => normalizeVenueForMap(venue))
                .filter(v => v && v.latitude != null && v.longitude != null);

              logger.log(`📥 searchEvents: ${normalizedVenues.length} venues normalized for map`);
              allResults.push(...normalizedVenues);
            }
          } else {
            logger.warn('⚠️ searchEvents: No valid venue IDs extracted from Typesense results');
          }
        } else if (venueResult?.hits?.length === 0) {
          logger.warn('⚠️ searchEvents: Venues search returned 0 results. Filter:', venueParams.filter_by);

          // Retry without language filter if no results (skip when caller wants genuine 0)
          if (language && venueFilters.length > 0 && !disableFallback) {
            logger.log('🔄 searchEvents: Retrying venues search without language filter...');
            const retryParams = {
              q: query || '*',
              query_by: 'venue_name,searchableContent',
              per_page: 250,
              sort_by: '_text_match:desc'
            };

            // Only apply location filter if it exists
            if (mapRadiusFilter && (query?.trim() || applyLocationFilterForAuto)) {
              retryParams.filter_by = `location:(${mapRadiusFilter})`;
            }

            const retryResult = await typesenseClient.collections('venues').documents().search(retryParams);
            logger.log('🔄 searchEvents: Retry result:', {
              found: retryResult?.found || 0,
              hits: retryResult?.hits?.length || 0
            });

            if (!signal?.aborted && retryResult?.hits?.length > 0) {
              const venueIds = retryResult.hits.map(hit => {
                const id = hit.document.venue_id || hit.document.id;
                const numId = typeof id === 'string' ? parseInt(id, 10) : id;
                return isNaN(numId) ? null : numId;
              }).filter(id => id !== null);

              if (venueIds.length > 0) {
                const backendResponse = await axios.post(`${apiUrl}/venues/batch`, {
                  venueIds: venueIds,
                  language: language
                }, signal ? { signal } : {});

                if (!signal?.aborted) {
                  const normalizedVenues = backendResponse.data
                    .map(venue => normalizeVenueForMap(venue))
                    .filter(v => v && v.latitude != null && v.longitude != null);

                  logger.log(`📥 searchEvents: ${normalizedVenues.length} venues from retry`);
                  allResults.push(...normalizedVenues);
                }
              }
            }
          }
        }
      } catch (venueErr) {
        logger.error('❌ searchEvents: Venues search error:', venueErr);
        logger.error('❌ Error details:', venueErr.message);
        if (venueErr.response) {
          logger.error('❌ Response data:', venueErr.response.data);
        }
      }
    }

    if (signal?.aborted) {
      return [];
    }

    logger.log(`📥 searchEvents total: ${allResults.length} results`);
    return allResults;

  } catch (error) {
    if (error.name === 'AbortError' || signal?.aborted) {
      logger.log('🚫 searchEvents: Request was aborted');
      return [];
    }

    logger.error('💥 searchEvents error:', error.response?.data || error.message);
    return [];
  }
}

/**
 * fetchFeedCandidates - request up to 10 candidate events for the scrolling feed
 *
 * @param {Object} options - Feed request options
 * @param {number} options.limit - Maximum number of items to fetch (max 10)
 * @param {string[]} options.exclude_ids - Array of event IDs already shown in this cycle
 * @param {string|null} options.cursor - Opaque server token for paging
 * @param {string|null} options.category - Category filter (null means 'Explore' - all categories)
 * @param {Object|null} options.geo - Geo filter { lat, lng, radiusKm }
 * @param {Array} options.lang_pref - Language preferences e.g., [{code:'TH', w:0.7},{code:'EN', w:0.2}]
 * @param {string[]} options.allow_langs - Languages to allow e.g., ['TH','EN','CN']
 * @param {number|null} options.profile_version - Server-side profile version last seen
 * @param {Object|null} options.deltas_since - Optional compact client deltas
 * @param {string} options.apiUrl - API base URL
 * @param {AbortSignal} options.signal - AbortSignal for request cancellation
 * @param {Object} options.logger - Logger object (defaults to console)
 * @returns {Promise<{items: Array, next_cursor: string|null, wrap_cycle?: number}>}
 */
export async function fetchFeedCandidates({
  limit = 10,
  exclude_ids = [],
  cursor = null,
  category = null,
  geo = null,
  lang_pref = [],
  allow_langs = [],
  userId = null,
  sessionId = null,
  profile_version = null,
  deltas_since = null,
  source = ['events'],          // NEW: Which collections to search
  excludeCategories = [],       // NEW: Categories to exclude
  apiUrl = process.env.REACT_APP_API_URL,
  signal,
  logger = console
} = {}) {
  if (!apiUrl) {
    logger.error('fetchFeedCandidates: missing API URL');
    return { items: [], next_cursor: null };
  }

  // Coerce and clamp limit to a number (≤10)
  const safeLimit = Math.min(10, Math.max(1, Number(limit) || 10));

  // Normalize allow_langs to UPPERCASE
  const normAllow = Array.isArray(allow_langs)
    ? allow_langs.map(s => String(s || '').toUpperCase())
    : [];

  const body = {
    limit: safeLimit,
    exclude_ids,
    cursor,
    category,
    geo,
    lang_pref,
    allow_langs: normAllow,
    userId,
    sessionId,
    profile_version,
    deltas_since,
    source: Array.isArray(source) ? source : [source || 'events'],
    excludeCategories: Array.isArray(excludeCategories) ? excludeCategories : []
  };

  try {
    const url = buildFeedUrl(apiUrl);
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      logger.error('fetchFeedCandidates failed', res.status, text);
      return { items: [], next_cursor: null };
    }

    return res.json(); // expected { items, next_cursor, wrap_cycle? }
  } catch (error) {
    if (error.name === 'AbortError' || signal?.aborted) {
      logger.log('🚫 fetchFeedCandidates: Request was aborted');
      return { items: [], next_cursor: null };
    }

    logger.error('💥 fetchFeedCandidates error:', error.message);
    return { items: [], next_cursor: null };
  }
}